import { BrowserModule } from '@angular/platform-browser';

import { ErrorHandler, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';

import { environment } from 'src/environments/environment';

import { ErrorComponent } from './Components/error/error.component';

import {
  MsalModule,
  MsalGuard,
  MsalInterceptor,
  MsalService,
  MsalBroadcastService,
  MsalRedirectComponent,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
} from '@azure/msal-angular';

import {
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
  IPublicClientApplication,
} from '@azure/msal-browser';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { HttpClientInterceptor } from 'src/app/Interceptors/http-client.interceptor';

import { MatButtonModule } from '@angular/material/button';

import { MatIconModule } from '@angular/material/icon';

import { MatListModule } from '@angular/material/list';

import { MatSidenavModule } from '@angular/material/sidenav';

import { MatToolbarModule } from '@angular/material/toolbar';

import { MatProgressBarModule } from '@angular/material/progress-bar';

import { MatMenuModule } from '@angular/material/menu';

import { MatDialogModule } from '@angular/material/dialog';

import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { MatFormFieldModule } from '@angular/material/form-field';

import { MatSelectModule } from '@angular/material/select';

import { MatOptionModule } from '@angular/material/core';

import { MatCardModule } from '@angular/material/card';

import { MatDividerModule } from '@angular/material/divider';

import { MatDatepickerModule } from '@angular/material/datepicker';

import { MatNativeDateModule } from '@angular/material/core';

import { MatCheckboxModule } from '@angular/material/checkbox';

import { MatTabsModule } from '@angular/material/tabs';

import { MatPaginatorModule } from '@angular/material/paginator';

import { MatTableModule } from '@angular/material/table';

import { MatTooltipModule } from '@angular/material/tooltip';

import { MatExpansionModule } from '@angular/material/expansion';

import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';

import { CommonModule } from '@angular/common';

import { AuthComponent } from './Components/auth/auth.component';

import { LoaderComponent } from './Components/loader/loader.component';

import { MyTeamViewComponent } from './Components/my-team-view/my-team-view.component';

import { ErrorHandlerService } from './Services/ErrorHandler/error-handler.service';

import { AccessDeniedComponent } from './Components/access-denied/access-denied.component';

import { SessionExpiredComponent } from './Components/session-expired/session-expired.component';

import { AppFooterComponent } from './Components/app-footer/app-footer.component';

import { HomeComponent } from './Components/home/home.component';

import { AuthErrorComponent } from './Components/auth-error/auth-error.component';

import { ReservationsComponent } from './Components/reservations/reservations.component';

import { RegisterComponent } from './Components/register/register.component';
import { SelfRegistrationComponent } from './Components/self-registration/self-registration.component';
import { RegistrationRequestDecisionComponent } from './Components/registration-request-decision/registration-request-decision.component';

import { CreateIssueComponent } from './Components/create-issue/create-issue.component';

import { EditUserFieldsComponent } from './Components/edit-user-fields/edit-user-fields.component';

import { UpdateProfileComponent } from './Components/update-profile/update-profile.component';

import { UnsupportedBrowserComponent } from './Components/unsupported-browser/unsupported-browser.component';

import { WhatsNewDialogComponent } from './Components/whats-new-dialog/whats-new-dialog.component';

import { SearchBarComponent } from './Components/search-bar/search-bar.component';

import { FilterDropdownComponent } from './Components/filter-dropdown/filter-dropdown.component';

import { ChipComponent } from './Components/chip/chip.component';

import { ErrorTooltipComponent } from './Components/error-tooltip/error-tooltip.component';

import { SharedModule } from './shared/shared.module';

import { provideNgxMask, NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,

      authority: 'https://login.microsoftonline.com/cddc1229-ac2a-4b97-b78a-0e5cacb5865c',

      redirectUri: environment.redirectUri,

      postLogoutRedirectUri: environment.redirectUri,
    },

    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },

    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
        },

        logLevel: LogLevel.Error,
      },
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,

    authRequest: {
      scopes: ['user.read', environment.impersonationUrl],
    },
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedMap = new Map<string, Array<string>>();

  protectedMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

  protectedMap.set(`${environment.apiBaseUrl}*`, [environment.impersonationUrl]);

  return {
    interactionType: InteractionType.Redirect,

    protectedResourceMap: protectedMap,
  };
}

@NgModule({
  declarations: [
    AppComponent,
    ErrorComponent,
    AuthComponent,
    LoaderComponent,
    MyTeamViewComponent,
    AccessDeniedComponent,
    SessionExpiredComponent,
    AppFooterComponent,
    HomeComponent,
    AuthErrorComponent,
    ReservationsComponent,
    RegisterComponent,
    SelfRegistrationComponent,
    RegistrationRequestDecisionComponent,
    CreateIssueComponent,
    EditUserFieldsComponent,
    UpdateProfileComponent,
    UnsupportedBrowserComponent,
    WhatsNewDialogComponent,
    SearchBarComponent,
    FilterDropdownComponent,
    ChipComponent,
    ErrorTooltipComponent,
  ],

  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    MsalModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FontAwesomeModule,
    SharedModule,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatMenuModule,
    MatDialogModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatCardModule,
    MatDividerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatTabsModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
    MatExpansionModule,
    NgxMatSelectSearchModule,
    NgxMaskDirective,
    NgxMaskPipe,
  ],

  providers: [
    {
      provide: HTTP_INTERCEPTORS,

      useClass: MsalInterceptor,

      multi: true,
    },

    {
      provide: HTTP_INTERCEPTORS,

      useClass: HttpClientInterceptor,

      multi: true,
    },

    {
      provide: MSAL_INSTANCE,

      useFactory: MSALInstanceFactory,
    },

    {
      provide: MSAL_GUARD_CONFIG,

      useFactory: MSALGuardConfigFactory,
    },

    {
      provide: MSAL_INTERCEPTOR_CONFIG,

      useFactory: MSALInterceptorConfigFactory,
    },

    {
      provide: ErrorHandler,

      useClass: ErrorHandlerService,
    },

    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,

      useValue: { floatLabel: 'always', appearance: 'outline' },
    },

    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        panelClass: 'ewrs-modal-panel',
        hasBackdrop: true,
        width: '480px',
        maxWidth: 'calc(100vw - 32px)',
      },
    },

    ...provideNgxMask(),

    MsalService,

    MsalGuard,

    MsalBroadcastService,
  ],

  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
